// src/services/buildUrl.js
//
// ROUTING-PHASE-1: URL builder helpers.
// Canonical mapping from legacy view-enum strings to URL paths, and
// convenience constructors for deep-links into specific pages.
// Replaces ad-hoc pushNavState({ view, projectId, … }) call-sites.

// Auxiliary protected tabs retain their legacy namespace; the three public
// market lenses are composed inside Places.
const MARKET_PLAN_TAB_TO_SEGMENT = {
  strategy:        "strategy",
  plan:            "plan",
  market_pressure: "pressure",
  whatif:          "scenario",
  recommendations: "guidance",
};

/**
 * Build the canonical URL for a Market Plan tab.
 * The Plan tab is Places → Markets. Pressure and Scenario are sibling lenses.
 * Market record links open the Markets drawer; Capacity keeps its own contract.
 */
export function buildMarketPlanUrl(tab, marketId) {
  const segment = MARKET_PLAN_TAB_TO_SEGMENT[tab] ?? "plan";
  // PLACES-CAPACITY-UNIFIED: Pressure and Scenario collapse into Capacity;
  // Scenario links open its panel, and a market is expanded via `open`.
  if (segment === "pressure" || segment === "scenario") {
    const params = new URLSearchParams();
    if (segment === "scenario") params.set("panel", "scenario");
    if (marketId) params.set("open", marketId);
    const q = params.toString();
    return `/places/capacity${q ? `?${q}` : ""}`;
  }
  const base = segment === "plan" ? "/places/markets" : `/markets/${segment}`;
  return marketId ? `${base}?${segment === "plan" ? "peek" : "expandMarket"}=${encodeURIComponent(marketId)}` : base;
}

// Canonical landing URL for the inline-expanded Projects list.
export function buildProjectsUrl(locationId = null) {
  return locationId
    ? `/projects?locationId=${encodeURIComponent(locationId)}`
    : "/projects";
}

export const PROJECT_TAB_SEGMENTS = Object.freeze(["portfolio", "weekly", "gantt", "financials", "risk-register", "participants", "signals", "contracts"]);

export function projectTabFromPath(pathname) {
  const segment = /^\/projects\/([^/]+)\/?$/.exec(pathname || "")?.[1];
  return PROJECT_TAB_SEGMENTS.includes(segment) ? segment : null;
}

// Preserve supported query/filter contracts when changing tabs or Weekly state.
export function buildProjectsTabUrl(tab, search = "", patch = {}) {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(patch)) {
    if (value == null) params.delete(key);
    else params.set(key, value);
  }
  if (tab !== "risk-register") params.delete("riskProject");
  const query = params.toString();
  return `/projects/${PROJECT_TAB_SEGMENTS.includes(tab) ? tab : "portfolio"}${query ? `?${query}` : ""}`;
}

export function buildProjectRiskRegisterUrl(projectId) {
  return buildProjectsTabUrl("risk-register", "", { riskProject: projectId });
}

// Reject rolled-over dates and non-Mondays; never normalize an invalid link
// into an invented week. Week keys follow the existing UTC calendar policy.
export function validProjectWeek(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value && date.getUTCDay() === 1
    ? value : null;
}

export function validWeeklyView(value) {
  if (value === "table") return "grid";
  return ["agenda", "grid"].includes(value) ? value : null;
}

// Map every legacy VIEW_* constant to its canonical URL path.
export const VIEW_TO_PATH = {
  places:                    "/places",
  home:                      "/home",
  portfolio:                 "/places/offices",
  leases:                    "/leases",
  plan:                      "/places/markets",
  projects:                  "/projects",
  okrs:                      "/okrs",
  "reports-landing":         "/reports",
  "report-wpr":              "/reports/wpr",
  "report-headcount-trend":  "/reports/headcount-trend",
  "report-open-roles":       "/reports/open-roles",
  "report-rto-compliance":   "/reports/rto-compliance",
  "report-foot-traffic":     "/reports/foot-traffic",
  "report-space-fit":        "/reports/space-fit",
  "report-cost-leakage":     "/reports/cost-leakage",
  "report-utilization":      "/reports/utilization",
  "report-data-coverage":    "/reports/data-coverage",
  "currency-rates":          "/currency-rates",
  admin:                     "/admin",
  "imports-queue":           "/admin/data/imports",
  "imports-session":         "/admin/data/import",
  // NAV-RESTRUCTURE-RADAR: "radar" nav key targets /infocus-radar; the old
  // /radar (Leadership Radar) stays accessible via the route but is no longer
  // a nav item. "infocus-radar" view key still resolves directly.
  radar:                     "/infocus-radar",
  "infocus-radar":           "/infocus-radar",
  playbooks:                 "/playbooks",
  decisions:                 "/radar/decisions",
  reviews:                   "/reviews",
  roadmap:                   "/roadmap",
  workflows:                 "/workflows",
  "start-here":             "/start-here",
  "data_health":             "/admin",
  "location-detail":         "/places/offices",
  "project-detail":          "/projects",
  "lease-detail":            "/leases",
};

/**
 * Convert a legacy view-enum string to the canonical URL path.
 * Falls back to the Offices lens for unknown view keys.
 */
export function viewToPath(viewKey) {
  return VIEW_TO_PATH[viewKey] ?? "/places/offices";
}

/**
 * Map a URL pathname to the NavRail active-view key.
 * Returns null when no match is found (callers can fall back to a prop).
 */
export function pathToNavView(pathname) {
  if (!pathname) return null;
  if (/^\/(?:places|offices|markets|market-plan)(?:\/|$)/.test(pathname)) return "places";
  if (pathname.startsWith("/home"))         return "home";
  if (pathname.startsWith("/offices"))      return "portfolio";
  if (pathname.startsWith("/leases"))       return "leases";
  // NAV-RESTRUCTURE-RADAR: both /infocus-radar and /radar highlight "radar" item.
  if (pathname.startsWith("/infocus-radar")) return "radar";
  if (pathname.startsWith("/radar"))         return "radar";
  if (pathname.startsWith("/playbooks"))     return "playbooks";
  if (pathname.startsWith("/decisions"))    return "radar";
  if (pathname.startsWith("/reviews"))      return "reviews";
  if (pathname.startsWith("/markets"))      return "plan";
  if (pathname.startsWith("/projects"))     return "projects";
  if (pathname.startsWith("/okrs"))         return "okrs";
  if (pathname.startsWith("/reports"))      return "reports-landing";
  if (pathname.startsWith("/admin") || pathname.startsWith("/imports")) return "admin";
  if (pathname.startsWith("/roadmap"))      return "roadmap";
  if (pathname.startsWith("/workflows"))    return "workflows";
  if (pathname.startsWith("/start-here"))  return "start-here";
  return null;
}

/**
 * Compute the back-button label from a returnPath string.
 */
export function returnPathToLabel(returnPath) {
  if (returnPath?.startsWith("/places/offices")) return "Offices";
  if (returnPath?.startsWith("/places/")) return "Places";
  if (!returnPath || returnPath === "/offices")             return "Offices";
  if (returnPath === "/leases")                             return "Leases";
  if (returnPath === "/home")                               return "Home";
  if (returnPath === "/reports")                            return "Reports";
  if (returnPath.startsWith("/reports"))                    return "Reports";
  if (returnPath === "/workflows")                          return "Actions";
  // PLACES-SLICE-2: a market record (/markets/<id>[/tab]) returns as "Market".
  if (/^\/markets\/(?!plan$|strategy$|pressure$|scenario$|guidance$)[^/]+(?:\/[^/]+)?$/.test(returnPath)) return "Market";
  if (returnPath === "/markets" || returnPath.startsWith("/markets/")) return "Market Plan";
  return "Offices";
}

/** Build a deep-link URL for a specific market detail page. */
export function buildMarketUrl(marketId) {
  return marketId ? `/places/markets?peek=${encodeURIComponent(marketId)}` : "/places/markets";
}

/** Build a deep-link URL for a specific lease detail page. */
export function buildLeaseUrl(leaseId) {
  return leaseId ? `/leases/${leaseId}` : "/leases";
}

/** Build a deep-link URL for a specific location. */
export function buildLocationUrl(locationId, tab = null) {
  if (!locationId) return "/places/offices";
  return tab ? `/offices/${locationId}/${tab}` : `/offices/${locationId}`;
}

/**
 * A project deep link opens its weekly reader, not the retired record page.
 * Only week and office context belong to this destination; old presentation,
 * filter and selection parameters cannot override the requested project.
 */
export function buildProjectUrl(projectId, search = "") {
  if (!projectId) return "/projects";
  const source = new URLSearchParams(search);
  const params = new URLSearchParams({ weeklyProject: projectId });
  const week = validProjectWeek(source.get("week")) || validProjectWeek(source.get("weeklyWeek"));
  if (week) params.set("week", week);
  const office = source.get("locationId");
  if (office) params.set("locationId", office);
  return `/projects/weekly?${params}`;
}

/** Build a deep-link URL for a specific import session. */
export function buildImportUrl(sessionId) {
  return sessionId ? `/admin/data/import/${sessionId}` : "/admin/data/imports";
}

/**
 * Build the canonical URL for the Leases page.
 * Portfolio retains the internal "list" key; Timeline has its own route.
 */
export function buildLeasesUrl(view) {
  if (!view || view === "list") return "/leases";
  if (view === "timeline") return "/leases/timeline";
  return "/leases";
}

export function isLeasesLandingPath(pathname) {
  return /^\/leases(?:\/timeline)?\/?$/.test(pathname);
}

// Own only the legacy presentation parameter, never unrelated bookmark context.
export function canonicalLeasesLocation(location) {
  const params = new URLSearchParams(location.search);
  const timeline = /^\/leases\/timeline\/?$/.test(location.pathname)
    || params.get("view") === "timeline"
    || (params.get("view") !== "list" && params.has("focus") && !location.state?.leasesPortfolio);
  params.delete("view");
  return buildLeasesUrl(timeline ? "timeline" : "list")
    + (params.size ? `?${params}` : "") + (location.hash || "");
}
